import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  FaTooth, FaCut, FaTint, FaTeeth, 
  FaTooth as FaRoot, FaChild,
  FaCheckCircle
} from 'react-icons/fa';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      icon: <FaTooth />,
      title: 'Dental Implants',
      description: 'Permanent, natural-looking solutions for missing teeth with state-of-the-art technology.',
      features: ['Permanent', 'Natural Look', '95% Success'],
      image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?ixlib=rb-4.0.3'
    },
    {
      icon: <FaCut />,
      title: 'Oral Surgery',
      description: 'Expert surgical procedures including wisdom tooth extraction with minimal discomfort.',
      features: ['Minimally Invasive', 'Quick Recovery', 'Expert Care'],
      image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?ixlib=rb-4.0.3'
    },
    {
      icon: <FaTint />,
      title: 'Teeth Whitening',
      description: 'Professional whitening treatments for a brighter, radiant smile safely.',
      features: ['Safe Formula', 'Instant Results', 'Long-lasting'],
      image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?ixlib=rb-4.0.3'
    },
    {
      icon: <FaTeeth />,
      title: 'Orthodontics',
      description: 'Modern braces and aligners to straighten teeth for children and adults.',
      features: ['Invisible Options', 'Comfortable', 'Fast Results'],
      image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?ixlib=rb-4.0.3'
    },
    {
      icon: <FaRoot />,
      title: 'Root Canal',
      description: 'Save damaged teeth with painless root canal therapy by skilled endodontists.',
      features: ['Painless', 'Save Natural Tooth', 'High Success'],
      image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3'
    },
    {
      icon: <FaChild />,
      title: 'Kids Dentistry',
      description: 'A friendly, gentle environment for children to develop healthy dental habits.',
      features: ['Fun Environment', 'Gentle Care', 'Preventive'],
      image: 'https://images.unsplash.com/photo-1629909615184-74f495363b67?ixlib=rb-4.0.3'
    }
  ];

  const styles = {
    services: {
      padding: isMobile ? '60px 0' : '100px 0',
      background: 'linear-gradient(135deg, #0A0F1C, #151E2F)',
      position: 'relative'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px' : '0 40px',
      position: 'relative',
      zIndex: 2
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: isMobile ? '30px' : '50px'
    },
    subtitle: {
      color: '#C8A97E',
      fontSize: isMobile ? '14px' : '16px',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '3px' : '4px',
      marginBottom: '10px',
      display: 'block',
      fontWeight: 600
    },
    h2: {
      fontSize: isMobile ? 'clamp(28px, 6vw, 36px)' : 'clamp(36px, 5vw, 48px)',
      color: '#ffffff',
      marginBottom: '15px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 800
    },
    titleGradient: {
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    divider: {
      width: isMobile ? '60px' : '80px',
      height: '3px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      margin: '0 auto',
      borderRadius: '2px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '20px' : '25px',
      marginTop: '30px'
    },
    card: {
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: '15px',
      overflow: 'hidden',
      border: '1px solid rgba(200, 169, 126, 0.15)',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s',
      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.3)'
    },
    cardImage: {
      height: isMobile ? '150px' : '180px',
      position: 'relative',
      overflow: 'hidden'
    },
    cardImg: {
      width: '100%',
      height: '100%',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      transition: 'transform 0.5s'
    },
    cardOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(to bottom, transparent, rgba(10, 15, 28, 0.9))'
    },
    cardContent: {
      padding: isMobile ? '20px' : '25px'
    },
    iconWrapper: {
      width: isMobile ? '50px' : '60px',
      height: isMobile ? '50px' : '60px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '24px' : '28px',
      color: '#0A0F1C',
      marginBottom: '15px',
      boxShadow: '0 10px 20px rgba(200, 169, 126, 0.3)'
    },
    cardTitle: {
      fontSize: isMobile ? '20px' : '22px',
      color: '#ffffff',
      marginBottom: '10px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 700
    },
    cardDesc: {
      color: '#A0A8B8',
      lineHeight: 1.6,
      marginBottom: '15px',
      fontSize: isMobile ? '13px' : '14px'
    },
    featuresList: {
      listStyle: 'none',
      marginBottom: '20px'
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#E8E8E8',
      fontSize: isMobile ? '12px' : '13px',
      marginBottom: '8px'
    },
    featureIcon: {
      color: '#C8A97E',
      fontSize: '14px'
    },
    cardBtn: {
      background: 'transparent',
      border: '2px solid #C8A97E',
      color: '#C8A97E',
      padding: isMobile ? '10px' : '12px',
      borderRadius: '50px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      transition: 'all 0.3s'
    }
  };

  return (
    <section id="services" style={styles.services} ref={ref}>
      <div style={styles.container}>
        <motion.div 
          style={styles.sectionHeader}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span style={styles.subtitle}>What We Offer</span>
          <h2 style={styles.h2}>
            Our <span style={styles.titleGradient}>Services</span>
          </h2>
          <div style={styles.divider}></div>
        </motion.div>

        <div style={styles.grid}>
          {services.map((service, index) => (
            <motion.div 
              key={index}
              style={styles.card}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ 
                y: -5,
                borderColor: '#C8A97E'
              }}
            >
              <div style={styles.cardImage}>
                <motion.div 
                  style={{
                    ...styles.cardImg,
                    backgroundImage: `url(${service.image})`
                  }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                ></motion.div>
                <div style={styles.cardOverlay}></div>
              </div>

              <div style={styles.cardContent}>
                <motion.div 
                  style={styles.iconWrapper}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  {service.icon}
                </motion.div>

                <h3 style={styles.cardTitle}>{service.title}</h3>
                <p style={styles.cardDesc}>{service.description}</p>

                <ul style={styles.featuresList}>
                  {service.features.map((feature, idx) => (
                    <motion.li 
                      key={idx}
                      style={styles.featureItem}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.5 + index * 0.1 + idx * 0.05 }}
                    >
                      <FaCheckCircle style={styles.featureIcon} />
                      {feature}
                    </motion.li>
                  ))}
                </ul>

                
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;